import { Component, OnInit, ViewChild, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { UsuariosService } from '../../services/usuario/usuarios.service';
import { DialogService } from '../../shared/services/dialog.service';
import { Usuario } from '../../shared/models/usuario.model';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { AuthImagePipe } from '../../pipes/auth-image.pipe';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
}

@Component({
  selector: 'app-usuarios',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule,
    AuthImagePipe
  ],
  providers: [ConfirmationService],
  templateUrl: './usuarios.component.html',
  styleUrls: ['./usuarios.component.scss']
})
export class UsuariosComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  // Signals para estado reactivo
  usuarios = signal<Usuario[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: Usuario[] = [];
  filterValue = signal<string>('');
  
  // Configuración
  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  // Control de suscripciones y reintentos
  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000; // 1 minuto

  // Columnas de la tabla
  columnsTable: Column[] = [
    { field: 'username', header: 'Usuario', fieldType: 'text', sortable: true, exportable: true, filtrable: true },
    { field: 'email', header: 'Email', fieldType: 'text', sortable: true, exportable: true, filtrable: true },
    { field: 'roles', header: 'Roles', sortable: false, exportable: true, filtrable: false },
    { field: 'enabled', header: 'Activo', fieldType: 'boolean', sortable: true, exportable: true, filtrable: true },
    { field: 'accion', header: 'Acciones' }
  ];

  // Para filtros y ordenamiento
  multiSortMeta: any[] = [];
  filtersMetadata: any = {};

  // Helper para limpiar tabla
  clearTableInProgress = false;
  clearTableCountLazyExecuted = 0;

  readonly roleDisplayMap: Record<string, { label: string; variant: string }> = {
    ROLE_ADMIN: { label: 'Administrador', variant: 'role-chip--admin' },
    ROLE_USER: { label: 'Usuario', variant: 'role-chip--user' },
    ROLE_VIEWER: { label: 'Solo Lectura', variant: 'role-chip--viewer' }
  };

  constructor(
    private usuariosService: UsuariosService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading = signal<boolean>(false);
    this.clearTableInProgress = false;
    this.clearTableCountLazyExecuted = 0;
    this.errorCount = 0;
    // No cargar aquí, loadLazy se encargará
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    // Manejo del bug de bucle infinito al hacer clear
    if (this.clearTableInProgress && this.clearTableCountLazyExecuted === 0) {
      this.clearTableCountLazyExecuted++;
      if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
        this.paginaActual = Math.floor(event.first / event.rows) + 1;
      } else {
        this.paginaActual = 1;
      }
      this.multiSortMeta = event.multiSortMeta || [];
      this.nroFilasPorPagina = event.rows ?? environment.REGISTROS_PER_PAGE ?? 10;
      this.filterValue.set(typeof event.globalFilter === 'string' ? event.globalFilter : '');
      this.filtersMetadata = event.filters || {};
      this.cargar();
    } else if (this.clearTableInProgress) {
      return;
    }

    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      // API Platform usa páginas basadas en 1, PrimeNG usa índice basado en 0
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }
    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue.set(typeof event.globalFilter === 'string' ? event.globalFilter : '');
    this.filtersMetadata = event.filters || {};
    this.cargar();
  }

  onGlobalFilter(value: string, table: Table | undefined): void {
    const term = value?.trim() ?? '';
    this.filterValue.set(term);
    table?.filterGlobal(term, 'contains');
  }

  cargar(): void {
    // Verificar si hemos excedido el límite de errores
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      // Si ha pasado más de 1 minuto desde el último error, resetear el contador
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);
    
    this.usuariosService.findAllUsuarios(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue(),
      this.multiSortMeta,
      this.filtersMetadata,
      ''
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (resp: any) => {
        this.loading.set(false);
        // Resetear contador de errores en caso de éxito
        this.errorCount = 0;
        this.lastErrorTimestamp = 0;
        
        const members = resp['hydra:member'] || resp.member || resp;
        
        // Mapear los datos del backend al formato del frontend
        const usuariosMapeados = members.map((user: any) => ({
          id: user.id,
          uuid: user.uuid,
          email: user.email,
          username: user.email?.split('@')[0] || user.email,
          fullName: user.name && user.lastName ? `${user.name} ${user.lastName}` : user.name || user.email,
          nombre: user.name,
          apellido: user.lastName,
          roles: user.roles,
          enabled: user.enabled,
          active: user.active,
          phone: user.phone,
          avatar: user.media?.id
            ? `${environment.URL_API.replace(/\/$/, '')}/file-manager?action=get&idMedia=${user.media.id}`
            : null,
          selected: false
        }));
        
        this.usuarios.set(usuariosMapeados);
        this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || usuariosMapeados.length);
        this.clearTableInProgress = false;
        this.clearTableCountLazyExecuted = 0;
      },
      error: (error: any) => {
        this.loading.set(false);
        this.clearTableInProgress = false;
        this.clearTableCountLazyExecuted = 0;
        
        // Incrementar contador de errores
        this.errorCount++;
        this.lastErrorTimestamp = Date.now();
        
        console.error(`Error al recuperar usuarios (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);
        
        const errorDetail = this.errorCount >= this.MAX_ERROR_COUNT
          ? 'Se alcanzó el límite de reintentos. Por favor, recarga la página.'
          : `Ocurrió un error al recuperar usuarios (intento ${this.errorCount}/${this.MAX_ERROR_COUNT})`;
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorDetail,
          life: this.errorCount >= this.MAX_ERROR_COUNT ? 8000 : 4000,
          sticky: this.errorCount >= this.MAX_ERROR_COUNT
        });
        
        // NO llamar a clear() aquí para evitar el ciclo infinito
        // En su lugar, limpiar los metadatos para evitar bucles de filtros/ordenamientos
        this.filtersMetadata = {};
        this.multiSortMeta = [];
        
        // Limpiar los datos en pantalla
        this.usuarios.set([]);
        this.totalRecords.set(0);
      }
    });
  }

  clear(table: Table): void {
    this.paginate = true;
    this.paginaActual = 0;
    this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE || 10;
    this.clearTableInProgress = true;
    this.filterValue.set('');
    this.selectedRows = [];
    // Resetear el contador de errores cuando el usuario limpia manualmente la tabla
    this.errorCount = 0;
    this.lastErrorTimestamp = 0;
    table.clear();
  }

  toggleUsuarioEnabled(usuario: Usuario): void {
    if (!usuario.id) return;
    
    // Cambiar 'enabled' para activar/desactivar al usuario
    // El backend automáticamente actualizará 'active' al mismo valor
    const usuarioActualizado: Usuario = {
      id: usuario.id,
      email: usuario.email,
      enabled: usuario.enabled
    };
    
    this.loading.set(true);
    this.usuariosService.guardarUsuario(usuarioActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: Usuario) => {
          this.loading.set(false);
          const accionTxt = resp.enabled ? 'activado' : 'desactivado';
          this.messageService.add({
            severity: 'success',
            summary: `Usuario ${accionTxt}`,
            detail: `El usuario ${resp.email} ha sido ${accionTxt}. Las banderas 'enabled' y 'active' se actualizaron correctamente.`,
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          // Revertir el cambio en caso de error
          usuario.enabled = !usuario.enabled;
          console.error('Error al actualizar usuario:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el estado del usuario',
            life: 3000
          });
        }
      });
  }

  editarUsuario(id: string): void {
    this.router.navigate(['/usuarios/edit', id]);
  }

  navigateToCreateUser(): void {
    this.router.navigate(['/usuarios/edit/nuevo']);
  }

  getRoleDisplay(role: string): string {
    const mapped = this.roleDisplayMap[role]?.label;
    if (mapped) {
      return mapped;
    }
    return role
      .replace(/^ROLE[_-]?/i, '')
      .split(/[_-]/g)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
      .join(' ') || 'Rol';
  }

  getRoleVariant(role: string): string {
    return this.roleDisplayMap[role]?.variant ?? 'role-chip--default';
  }

}
